import {PublisherInterface} from "./publisher";
import logging from "../utils/log";
import {Application} from "../application/application";
import webAPI from "../api/web-api-2";

interface DeviceInfo {
    mdi : MediaDeviceInfo;
    name : string;
}

interface AggregateDeviceInfo {
    cams: DeviceInfo[];
    mics: DeviceInfo[];
    [key: string]: DeviceInfo[];
}

interface candidateInfo {
    roomId: string
    candidate: RTCIceCandidate
}

export class WebRtcPublisherNg implements PublisherInterface {
    private TAG = "[WebRtcPublisherNg]";
    private app : Application;
    private roomId : string;
    private video : HTMLVideoElement;
    private aggregateDeviceInfo : AggregateDeviceInfo;
    private mediaStream: MediaStream;
    private peerConnection : RTCPeerConnection = null;
    private candidate : candidateInfo[] = [];

    constructor(private $root : JQuery, private containerClass : string = '.sxcccp-video') {
    }

    async init(app: Application): Promise<void> {
        let room = app.chatServer.currentRoom ? app.chatServer.currentRoom  : app.chatServer.lastRoom;
        this.app = app;
        this.roomId = room.getID();
        this.video = document.createElement('video');
        this.video.muted = true;
        this.$root.find(this.containerClass).empty().append(this.video);

        try {
            this.aggregateDeviceInfo = await this.enumerateDevices();
        } catch (err) {
            logging.error(this.TAG, err);
            throw new Error('Device enumeration failed.');
        }
    }

    async askForCam(_camID : number = 0, _micID : number = 0): Promise<any> {
        const navigator = window.navigator;
        let permission;

        //if (navigator.permissions) {
        //    permission = await navigator.permissions.query({name: 'camera'})
        //        .then((permissionObj) => {
        //            switch (permissionObj.state) {
        //                case 'denied':
        //                    logging.error(this.TAG, permissionObj.state);
        //                    return Promise.reject('alreadyBlocked');
        //                case 'granted':
        //                case 'prompt':
        //                    return Promise.resolve();
        //            }
        //        })
        //        .catch((err) => {
        //            logging.error(this.TAG, err);
        //            return Promise.reject('technical');
        //        });
        //}

        //if (permission !== undefined) {
        //    return permission;
        //}

        let askTime = Date.now();

        return navigator.mediaDevices.getUserMedia(this.getConstraints(_camID, _micID))
            .then(this.connectStreamToElement.bind(this))
            .catch(err => {
                if (err instanceof DOMException) {
                    // navigator.mediaDevices.getUserMedia() failed
                    switch (err.name) {
                        case 'NotAllowedError': // user has denied access
                            if ((Date.now() - askTime < 1000)) {
                                return Promise.reject('alreadyBlocked');
                            } else {
                                return Promise.reject('blocked');
                            }
                        case 'AbortError': // ???, don't know why, but blew up
                        case 'NotFoundError': // no media track of specified type in constraints
                        case 'NotReadableError': // HW error
                        case 'OverconstrainedError': // constraints asked for cannot be met by device
                        case 'SecurityError': // UA disabled getUserMedia() on the Document
                        case 'TypeError': // empty constraints or insecure context
                        default:
                            logging.error(this.TAG, err.name);
                            return Promise.reject('technical');

                    }
                } else {
                    // HTMLMediaElement.play() failed
                    switch (err) {
                        case 'NotAllowedError': // programmatic playback is not allowed in context
                        case 'NotSupportedError': // unsupported format
                        default:
                            logging.error(this.TAG, err);
                            return Promise.reject('playFailed');
                    }
                }
            });
    }

    getCamList(): string[] {
        return this.aggregateDeviceInfo.cams.map(x => x.name);
    }

    getMicList(): string[] {
        return this.aggregateDeviceInfo.mics.map(x => x.name);
    }

    mute(muted: boolean): void {
        if (this.mediaStream != null && 'getAudioTracks' in this.mediaStream) {
            this.mediaStream.getAudioTracks().forEach((track) => {
                track.enabled = !muted;
            });
        }
    }

    showPreview(show: boolean): void {
        $(this.video)[show ? "show" : "hide"]();
    }

    publish(streamName: string = ''): Promise<any> {
        this.initSignaling();

        return this.initPeerConnection();
    }

    unpublish(): Promise<void> {
        let peerConnection = this.peerConnection;

        this.app.chatServer.evt.off('cam2camSignaling');

        peerConnection && peerConnection.connectionState !== 'closed' && peerConnection.close();
        this.peerConnection = null;

        if (this.video) {
            this.video.pause();
            this.video.src = '';
            //this.video = null;
        }

        if (this.mediaStream) {
            for (let track of this.mediaStream.getTracks()) {
                track.stop();
            }
            this.mediaStream = null;
        }

        return Promise.resolve();
    }

    destroy(): Promise<void> {
        return this.unpublish().finally(() => {
            this.$root.find(".sxcccp-video").empty();
        });
    }

    takeSnapshot(quality: number): string {
        if (!this.video) return null;

        const canvas = document.createElement('canvas');
        canvas.width  = this.video.videoWidth;
        canvas.height = this.video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(this.video, 0, 0);

        let data = canvas.toDataURL('image/jpeg').split(",", 2);
        return data[1];
    }

    private getConstraints(_camID : number = 0, _micID : number = 0) : MediaStreamConstraints {
        return {
            video: {
                width: {
                    min: 320,
                    ideal: 384,
                    max: 640,
                },
                height: {
                    min: 180,
                    ideal: 216,
                    max: 360,
                },
                frameRate: {
                    ideal: 15,
                    max: 30
                },
                aspectRatio: 16/9,
                deviceId: this.aggregateDeviceInfo.cams[_camID].mdi.deviceId
            },
            audio: {
                deviceId: this.aggregateDeviceInfo.mics[_micID].mdi.deviceId
            }
        };
    }

    private enumerateDevices(): Promise<any> {
        if (!navigator.mediaDevices) {
            return Promise.reject(new Error('Document wasn\'t loaded securely.'));
        }

        return navigator.mediaDevices.enumerateDevices()
            .then((devices: MediaDeviceInfo[]): AggregateDeviceInfo => {
                const cams: DeviceInfo[] = [];
                const mics: DeviceInfo[] = [];

                for (let device of devices) {
                    switch (device.kind) {
                        case "audioinput": {
                            mics.push({
                                mdi: device,
                                name: device.label || ("Mic #" + (mics.length + 1))
                            });
                            break;
                        }

                        case "videoinput": {
                            cams.push({
                                mdi: device,
                                name: device.label || ("Cam #" + (cams.length + 1))
                            })
                        }
                    }
                }

                return {
                    cams: cams,
                    mics: mics
                };
            });
    }

    private initSignaling() {
        const server = this.app.chatServer;

        server.evt.on('cam2camSignaling', (params) => {
            const peerConnection = this.peerConnection;
            const roomId = params.roomid;

            switch (params.type) {
                case 'newIceCandidate':
                    if (peerConnection instanceof RTCPeerConnection) {
                        peerConnection.addIceCandidate(params.candidate as RTCIceCandidate)
                            .catch((err : DOMException) => {
                                switch (err.name) {
                                    case 'TypeError':
                                    case 'InvalidStateError':
                                    case 'OperationError':
                                    default:
                                        logging.error(this.TAG, 'peerConnection.addIceCandidate', err);
                                        // TODO cam2cam: handle peerConnection.addIceCandidate error
                                }
                            });
                    } else {
                        logging.debug(this.TAG, 'peerConnection.addIceCandidate', 'should not reach here');
                    }

                    break;
                case 'performerReady':
                    this.peerConnection.createOffer()
                        .then(description => {
                            peerConnection.setLocalDescription(description).then(() => {
                                server.camToCamSignaling(roomId, {
                                    type: 'offer',
                                    description: description
                                });
                            });
                        })
                        .catch((reason : string) => {
                            switch (reason) {
                                case 'InvalidStateError': // RTCPeerConnection is closed.
                                case 'NotReadableError': // No cert
                                case 'OperationError': // Resource check failed
                                default:
                                    logging.error(this.TAG, 'peerConnection.createOffer', reason);
                                // TODO cam2cam: handle peerConnection.createOffer error
                            }
                        });

                    break;
                case 'answer':
                    peerConnection.setRemoteDescription(params.description as RTCSessionDescriptionInit)
                        .catch((err) => {
                            logging.error(this.TAG, 'peerConnection.setRemoteDescription', err);
                            // TODO cam2cam: handle peerConnection.setRemoteDescription error
                        });
            }
        });
    }

    private initPeerConnection(): Promise<any> {
        return new Promise((resolve, reject) => {
            //webAPI.get('turnserver/getAuthData', {debug: '1', unType: 'extende'}).then((r: any) => {
            webAPI.get('turnserver/getAuthData', {unType: 'extended'}).then((r: any) => {
                let peerConnection = this.peerConnection = new RTCPeerConnection({
                    iceServers: [{
                        urls: 'turn:cam2cam.apn2.com:3478',
                        username: r.username,
                        credential: r.password
                    }],
                    iceTransportPolicy: 'relay'
                });
                const roomId = this.roomId;

                peerConnection.onicecandidateerror = (event: Event) => {
                    logging.error(this.TAG, "initPeerConnection.ice.error", event);
                    reject(event as RTCPeerConnectionIceErrorEvent);
                };

                peerConnection.onicegatheringstatechange = (event: Event) => {
                    logging.debug(this.TAG, "initPeerConnection.ice.statechange", event);
                };

                peerConnection.onicecandidate = (event: RTCPeerConnectionIceEvent) => {
                    const candidate = event.candidate;

                    if (candidate) {
                        this.app.chatServer.camToCamSignaling(roomId, {
                            type: 'newIceCandidate',
                            candidate: candidate
                        });
                    }
                };

                if ('addTrack' in this.peerConnection) {
                    for (let track of this.mediaStream.getTracks()) {
                        peerConnection.addTrack(track, this.mediaStream);
                    }
                } else {
                    // @ts-ignore
                    peerConnection.addStream(this.mediaStream);
                }

                resolve();
            });
        });
    }

    private connectStreamToElement(src: MediaStream): Promise<any> {
        this.video.srcObject = this.mediaStream = src;

        return this.video.play();
    }
}
